import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { format, startOfWeek, addDays } from 'date-fns';

const STATUS_STYLES = {
  scheduled: { background: '#EFF6FF', color: '#2563EB', border: '#BFDBFE', label: 'Programado' },
  pending:   { background: '#FFFBEB', color: '#D97706', border: '#FDE68A', label: 'Pendiente'  },
  active:    { background: '#F0FDF4', color: '#16A34A', border: '#BBF7D0', label: 'Activo'     },
  live:      { background: '#F0FDF4', color: '#16A34A', border: '#BBF7D0', label: 'En vivo'    },
  open:      { background: '#F0FDF4', color: '#16A34A', border: '#BBF7D0', label: 'Abierto'    },
  closed:    { background: '#F0F2F8', color: '#6B7A99', border: '#CBD0DB', label: 'Cerrado'    },
  finished:  { background: '#F0F2F8', color: '#6B7A99', border: '#CBD0DB', label: 'Finalizado' },
  ended:     { background: '#F0F2F8', color: '#6B7A99', border: '#CBD0DB', label: 'Finalizado' },
  cancelled: { background: '#FEF2F2', color: '#DC2626', border: '#FECACA', label: 'Cancelado'  },
  canceled:  { background: '#FEF2F2', color: '#DC2626', border: '#FECACA', label: 'Cancelado'  },
};

const DEFAULT_STYLE = { background: '#F8F9FC', color: '#6B7A99', border: '#E2E5EC', label: 'Próximo' };

function getStatusStyle(status) {
  return STATUS_STYLES[(status || '').toLowerCase()] || DEFAULT_STYLE;
}

function buildWeeks() {
  const today = new Date();
  const thisMonday = startOfWeek(today, { weekStartsOn: 1 });
  return [
    { label: 'Semana pasada',   key: 'past',    start: addDays(thisMonday, -7), end: thisMonday,            isPast: true  },
    { label: 'Esta semana',     key: 'current', start: thisMonday,              end: addDays(thisMonday, 7), isCurrent: true },
    { label: 'Próxima semana',  key: 'next',    start: addDays(thisMonday, 7),  end: addDays(thisMonday, 14) },
  ];
}

function AuctionItem({ auction }) {
  const style = getStatusStyle(auction.status);
  const date = new Date(auction.start_date);
  const href = `https://${auction.tenant_domain}/auctions/${auction.id}/showroom`;

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        display: 'block',
        background: style.background,
        border: `1px solid ${style.border}`,
        borderLeft: `3px solid ${style.border}`,
        borderRadius: 8,
        padding: '10px 12px',
        marginBottom: 8,
        textDecoration: 'none',
        transition: 'opacity 0.15s',
        boxShadow: '0 1px 2px rgba(2,19,68,0.04)',
      }}
      onMouseEnter={e => { e.currentTarget.style.opacity = '0.75'; }}
      onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4, gap: 6 }}>
        <span style={{
          fontSize: '0.6875rem',
          fontWeight: 500,
          color: '#6B7A99',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
        }}>
          {auction.tenant_name || '—'}
        </span>
        <span style={{
          flexShrink: 0,
          fontSize: '0.625rem',
          fontWeight: 600,
          padding: '2px 7px',
          borderRadius: 9999,
          background: style.border,
          color: style.color,
        }}>
          {style.label}
        </span>
      </div>
      <div style={{ fontSize: '0.75rem', color: '#4A5568', marginBottom: 4, fontWeight: 400 }}>
        {format(date, 'EEEE, MMM d')} · {format(date, 'h:mm a')}
      </div>
      <div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#021344', lineHeight: 1.3, marginBottom: 4 }}>
        {auction.name}
      </div>
      <div style={{ display: 'flex', gap: 8, fontSize: '0.6875rem', color: '#9AA3B5' }}>
        {auction.lot_count > 0 && (
          <span>{auction.lot_count} lotes</span>
        )}
        <span>{auction.participant_count || 0} participantes</span>
      </div>
    </a>
  );
}

AuctionItem.propTypes = {
  auction: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    start_date: PropTypes.string.isRequired,
    status: PropTypes.string,
    tenant_name: PropTypes.string,
    tenant_domain: PropTypes.string,
    participant_count: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    lot_count: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }).isRequired,
};

function WeekColumn({ week, auctions }) {
  const headerColor = week.isCurrent ? '#1F50FF' : week.isPast ? '#CBD0DB' : '#021344';

  return (
    <div style={{
      background: '#fff',
      border: `1px solid ${week.isCurrent ? '#1F50FF' : '#E2E5EC'}`,
      boxShadow: week.isCurrent
        ? '0 0 0 4px rgba(31,80,255,0.08), 0 4px 12px rgba(2,19,68,0.06)'
        : '0 1px 3px rgba(2,19,68,0.05)',
      borderRadius: 12,
      padding: '18px 16px',
      minHeight: 200,
      display: 'flex',
      flexDirection: 'column',
    }}>
      <div style={{ marginBottom: 16 }}>
        <h3 style={{
          margin: 0,
          fontSize: '0.8125rem',
          fontWeight: 600,
          color: headerColor,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
        }}>
          {week.label}
        </h3>
        <p style={{ margin: '4px 0 0', fontSize: '0.75rem', color: '#9AA3B5' }}>
          {format(week.start, 'MMM d')} – {format(addDays(week.end, -1), 'MMM d, yyyy')}
        </p>
      </div>

      <div style={{ flex: 1 }}>
        {auctions.length === 0 ? (
          <div style={{ color: '#CBD0DB', fontSize: '0.8125rem', textAlign: 'center', paddingTop: 32 }}>
            Sin remates
          </div>
        ) : (
          auctions.map(a => <AuctionItem key={a.id} auction={a} />)
        )}
      </div>

      {auctions.length > 0 && (
        <div style={{ marginTop: 8, fontSize: '0.6875rem', color: '#CBD0DB', textAlign: 'right' }}>
          {auctions.length} remate{auctions.length !== 1 ? 's' : ''}
        </div>
      )}
    </div>
  );
}

WeekColumn.propTypes = {
  week: PropTypes.shape({
    label: PropTypes.string.isRequired,
    start: PropTypes.instanceOf(Date).isRequired,
    end: PropTypes.instanceOf(Date).isRequired,
    isCurrent: PropTypes.bool,
    isPast: PropTypes.bool,
  }).isRequired,
  auctions: PropTypes.array.isRequired,
};

/**
 * Displays auctions across last, current, and next week in three columns.
 */
function ThreeWeekCalendar({ auctions }) {
  const weeks = useMemo(buildWeeks, []);

  const weekAuctions = useMemo(() => weeks.map(week =>
    auctions.filter(a => {
      const d = new Date(a.start_date);
      return d >= week.start && d < week.end;
    })
  ), [auctions, weeks]);

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
      {weeks.map((week, i) => (
        <WeekColumn key={week.key} week={week} auctions={weekAuctions[i]} />
      ))}
    </div>
  );
}

ThreeWeekCalendar.propTypes = {
  auctions: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    start_date: PropTypes.string.isRequired,
    status: PropTypes.string,
    tenant_name: PropTypes.string,
    tenant_domain: PropTypes.string,
    participant_count: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    lot_count: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  })).isRequired,
};

export default ThreeWeekCalendar;
